import { supabase } from "@/integrations/supabase/client";
import { translateSpanishUnits } from "@/lib/measurementConverter";

const TEXT_CANDIDATE_KEYS = ["text", "description", "instruction", "title", "label", "name", "value"] as const;

function normalizeTextValue(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();

  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;

    // Ingredient objects ({quantity, unit, name}) must be checked FIRST —
    // before the generic key scan — otherwise "name" in TEXT_CANDIDATE_KEYS
    // would match and return only the ingredient name, stripping the quantity.
    if (obj.quantity != null || obj.unit != null) {
      const ingredientName = obj.name ?? obj.text ?? obj.label ?? obj.value;
      // Only include unit when a quantity is also present — a unit with no quantity
      // produces nonsense prefixes like "S coconut flour" from bad DB values.
      const hasQuantity = obj.quantity != null && String(obj.quantity).trim() !== "";
      const parts = [obj.quantity, hasQuantity ? obj.unit : null, ingredientName]
        .filter((part) => part != null && String(part).trim())
        .map((part) => String(part).trim());
      if (parts.length > 0) return parts.join(" ");
    }

    for (const key of TEXT_CANDIDATE_KEYS) {
      const candidate = obj[key];
      if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
      if (typeof candidate === "number" || typeof candidate === "boolean") return String(candidate).trim();
    }
  }

  return "";
}

function sanitizeListItem(value: string): string {
  return value
    .replace(/^\d+[\.\)\-:]\s*/, "")
    .replace(/^[•*\-]\s*/, "")
    .trim();
}

function splitLongSentenceBlock(text: string): string[] {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return [];

  const matches = normalized.match(/[^.!?]+[.!?]?/g) ?? [normalized];
  return matches.map((item) => sanitizeListItem(item)).filter(Boolean);
}

export function normalizeRecipeIngredients(value: unknown): string[] {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        const normalized = normalizeTextValue(item);
        return normalized ? normalized.split("|") : [];
      })
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      const normalizedParsed = normalizeRecipeIngredients(parsed);
      if (normalizedParsed.length > 0) return normalizedParsed;
    } catch {
      // ignore invalid JSON and fall back to text parsing
    }

    return trimmed
      .split(/\r?\n|,|\|/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  const normalized = normalizeTextValue(value);
  return normalized ? [normalized] : [];
}

export function normalizeRecipeSteps(value: unknown): string[] {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        const normalized = normalizeTextValue(item);
        return normalized ? normalized.split(/\\n|\n|\|/) : [];
      })
      .map((item) => sanitizeListItem(item))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      const normalizedParsed = normalizeRecipeSteps(parsed);
      if (normalizedParsed.length > 0) return normalizedParsed;
    } catch {
      // ignore invalid JSON and fall back to text parsing
    }

    let steps = trimmed
      .replace(/\r/g, "")
      .split(/\\n|\n|\|/)
      .map((item) => sanitizeListItem(item))
      .filter(Boolean);

    if (steps.length <= 1 && trimmed.length > 140) {
      steps = splitLongSentenceBlock(trimmed);
    }

    return steps;
  }

  const normalized = normalizeTextValue(value);
  return normalized ? normalizeRecipeSteps(normalized) : [];
}

const normalizeComparableValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeComparableValue(item)).join("|");
  }

  if (value == null) return "";

  return String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const TRANSLATABLE_RECIPE_KEYS = [
  "title",
  "category",
  "description",
  "ingredients",
  "preparation",
  "chef_tip",
  "difficulty",
  "dish_type",
  "meal_moment",
  "servings",
  "time",
  "prep_time",
  "cook_time",
  "total_time",
  "glycemic_notes",
  "peak_time_text",
  "glucose_rise_text",
  "tags",
  "dish_characteristics",
  "cuisine_origin",
] as const;

const hasMeaningfulRecipeTranslation = (
  sourceRecipe: Record<string, unknown>,
  translatedRecipe: Record<string, unknown>,
) => {
  return TRANSLATABLE_RECIPE_KEYS.some((key) => {
    return normalizeComparableValue(sourceRecipe[key]) !== normalizeComparableValue(translatedRecipe[key]);
  });
};

const translatedRecipeCache = new Map<string, Promise<any | null>>();

const translateRecipeFallback = async (recipe: any, targetLanguage: string) => {
  const cacheKey = `${recipe.id}:${targetLanguage}`;
  const cachedPromise = translatedRecipeCache.get(cacheKey);

  if (cachedPromise) {
    const cachedRecipe = await cachedPromise;
    if (cachedRecipe) return cachedRecipe;
    translatedRecipeCache.delete(cacheKey);
  }

  const translationPromise = (async () => {
    try {
      const fields = {
        title: recipe.title || "",
        category: recipe.category || "",
        description: recipe.description || "",
        ingredients: recipe.ingredients || [],
        preparation: recipe.preparation || "",
        chef_tip: recipe.chef_tip || "",
        difficulty: recipe.difficulty || "",
        dish_type: recipe.dish_type || "",
        meal_moment: recipe.meal_moment || "",
        servings: recipe.servings || "",
        time: recipe.time || "",
        prep_time: recipe.prep_time || "",
        cook_time: recipe.cook_time || "",
        total_time: recipe.total_time || "",
        glycemic_notes: recipe.glycemic_notes || "",
        peak_time_text: recipe.peak_time_text || "",
        glucose_rise_text: recipe.glucose_rise_text || "",
        tags: recipe.tags || [],
        dish_characteristics: recipe.dish_characteristics || [],
        cuisine_origin: recipe.cuisine_origin || "",
      };

      const { data, error } = await supabase.functions.invoke("translate-content", {
        body: { fields, targetLang: targetLanguage, context: "recipe" },
      });

      const translatedFields = data?.translated;

      if (error || data?.error || data?.fallback || !translatedFields) {
        return null;
      }

      const translatedRecipe = {
        ...recipe,
        ...translatedFields,
        language: targetLanguage,
      };

      if (!hasMeaningfulRecipeTranslation(recipe, translatedRecipe)) {
        return null;
      }

      return translatedRecipe;
    } catch {
      return null;
    }
  })();

  translatedRecipeCache.set(cacheKey, translationPromise);

  const translatedRecipe = await translationPromise;

  if (!translatedRecipe) {
    translatedRecipeCache.delete(cacheKey);
  }

  return translatedRecipe;
};

/**
 * Validate that a persistent translation has the essential fields.
 * A translation is only "complete" if it has title + ingredients + preparation.
 */
const isTranslationComplete = (data: any): { complete: boolean; missingFields: string[] } => {
  const missingFields: string[] = [];
  if (!data.title) missingFields.push("title");
  const ing = data.ingredients;
  const hasIngredients = Array.isArray(ing) && ing.length > 0;
  if (!hasIngredients) missingFields.push("ingredients");
  const prep = data.preparation;
  const hasPreparation = (Array.isArray(prep) && prep.length > 0) || (typeof prep === "string" && prep.trim().length > 0);
  if (!hasPreparation) missingFields.push("preparation");
  return { complete: missingFields.length === 0, missingFields };
};

/**
 * Apply a persistent translation from recipe_translations table onto the base recipe.
 * Returns null if no translation exists.
 */
const applyPersistentTranslation = async (recipe: any, targetLanguage: string): Promise<any | null> => {
  const { data, error } = await supabase
    .from("recipe_translations")
    .select("*")
    .eq("recipe_id", recipe.id)
    .eq("language", targetLanguage)
    .maybeSingle();

  if (error || !data) return null;

  const { complete, missingFields } = isTranslationComplete(data);

  if (!complete) {
    const aiTranslated = await translateRecipeFallback(recipe, targetLanguage);
    if (aiTranslated) {
      const translatedIngredients = missingFields.includes("ingredients")
        ? normalizeRecipeIngredients(aiTranslated.ingredients)
        : normalizeRecipeIngredients(data.ingredients);
      const translatedPreparation = missingFields.includes("preparation")
        ? normalizeRecipeSteps(aiTranslated.preparation).join("\n")
        : normalizeRecipeSteps(data.preparation).join("\n");

      return {
        ...aiTranslated,
        title: data.title || aiTranslated.title,
        description: data.description || aiTranslated.description,
        chef_tip: data.chef_tip || aiTranslated.chef_tip,
        glycemic_notes: data.glycemic_notes || aiTranslated.glycemic_notes,
        peak_time_text: data.peak_time_text || aiTranslated.peak_time_text,
        glucose_rise_text: data.glucose_rise_text || aiTranslated.glucose_rise_text,
        ingredients: translatedIngredients,
        preparation: translatedPreparation,
        tags: Array.isArray(data.tags) && data.tags.length > 0 ? data.tags : aiTranslated.tags,
        language: targetLanguage,
      };
    }

    // AI fallback failed and the DB translation is incomplete (missing title,
    // ingredients, and/or preparation) — applying only the fields that ARE
    // present would mix languages (e.g. translated title + original-language
    // ingredients). Return null so the caller keeps the recipe in its
    // original language instead, matching useLocalizedRecipes.ts's
    // all-or-nothing rule.
    return null;
  }

  let normalizedIngredients = recipe.ingredients;
  const translatedIngredients = normalizeRecipeIngredients(data.ingredients);
  if (translatedIngredients.length > 0) {
    normalizedIngredients = translatedIngredients;
  }

  let normalizedPreparation = recipe.preparation;
  const translatedPreparation = normalizeRecipeSteps(data.preparation);
  if (translatedPreparation.length > 0) {
    normalizedPreparation = translatedPreparation.join("\n");
  }

  let normalizedTags: any = ensureSafeArray(recipe.tags);
  if (data.tags) {
    normalizedTags = ensureSafeArray(data.tags);
  }

  return {
    ...recipe,
    title: data.title || recipe.title,
    description: data.description || recipe.description,
    chef_tip: data.chef_tip || recipe.chef_tip,
    glycemic_notes: data.glycemic_notes || recipe.glycemic_notes,
    peak_time_text: data.peak_time_text || recipe.peak_time_text,
    glucose_rise_text: data.glucose_rise_text || recipe.glucose_rise_text,
    ingredients: normalizedIngredients,
    preparation: normalizedPreparation,
    tags: normalizedTags,
    language: targetLanguage,
  };
};

/**
 * Safely normalizes a tags-like value into a string[] regardless of storage format.
 * Handles: string[], {custom: string[]}, null/undefined, single string.
 */
function ensureSafeArray(val: unknown): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter((v) => typeof v === "string" && v.trim());
  if (typeof val === "object" && val !== null) {
    // Handle {custom: [...]} format from some translations
    const obj = val as Record<string, unknown>;
    if (Array.isArray(obj.custom)) return obj.custom.filter((v: any) => typeof v === "string" && v.trim());
    // Try to extract string values from object keys
    return Object.keys(obj).filter((k) => typeof k === "string" && k.trim());
  }
  if (typeof val === "string") return val.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
}

/**
 * Check if a string looks like a UUID.
 */
const isUUID = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

/**
 * Extract a trailing UUID from route params like
 * "walnut-bread--123e4567-e89b-12d3-a456-426614174000".
 */
export const extractRouteRecipeId = (value: string): string | null => {
  const match = value.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i);
  return match?.[1] ?? null;
};

/**
 * Resolve a recipe by slug (title slug) within a category.
 * Returns the first matching recipe.
 */
const resolveRecipeBySlug = async (slug: string, categorySlug?: string): Promise<any | null> => {
  const { nameToSlug } = await import("@/lib/collectionSlugs");
  
  // Build query — fetch candidates
  let query = supabase.from("recipes").select("*");
  
  // If we have a category slug, filter candidates
  const { data: candidates, error } = await query;
  if (error || !candidates) return null;
  
  const titleMatches = candidates.filter((r: any) => r.title && nameToSlug(r.title) === slug);
  if (titleMatches.length === 0) return null;

  if (categorySlug) {
    const categoryMatch = titleMatches.find((r: any) => nameToSlug(r.category) === categorySlug);
    if (categoryMatch) return categoryMatch;
  }

  return titleMatches[0] || null;
};

export const getLocalizedRecipeById = async (recipeId: string, targetLanguage: string, categorySlug?: string) => {
  try {
    let baseRecipe: any = null;
    let baseError: any = null;
    const routeRecipeId = extractRouteRecipeId(recipeId);
    const lookupId = routeRecipeId || recipeId;
    
    if (isUUID(lookupId)) {
      // Direct UUID lookup
      const result = await supabase
        .from("recipes")
        .select("*")
        .eq("id", lookupId)
        .maybeSingle();
      baseRecipe = result.data;
      baseError = result.error;
    } else {
      // Slug-based lookup
      baseRecipe = await resolveRecipeBySlug(lookupId, categorySlug);
    }

    if (baseError || !baseRecipe) {
      return null;
    }

    // INTEGRITY CHECK: Verify the fetched recipe matches the requested ID (only for UUID lookups)
    if (isUUID(lookupId) && baseRecipe.id !== lookupId) {
      return null;
    }

    const persistentTranslation = await applyPersistentTranslation(baseRecipe, targetLanguage);
    if (persistentTranslation) return sanitizeRecipeOutput(persistentTranslation, targetLanguage);

    // Never swap to a different recipe using non-unique fields like image_group,
    // module_id, collection_id or sort_order. If no stable translation exists,
    // keep the original recipe and translate that exact record if needed.
    if (baseRecipe.language === targetLanguage) {
      return sanitizeRecipeOutput(baseRecipe, targetLanguage);
    }

    const aiTranslation = await translateRecipeFallback(baseRecipe, targetLanguage);
    if (aiTranslation) {
      // Persist the AI translation to recipe_translations so future views skip re-translating
      supabase
        .from("recipe_translations")
        .upsert({
          recipe_id: baseRecipe.id,
          language: targetLanguage,
          title: aiTranslation.title || null,
          description: aiTranslation.description || null,
          ingredients: aiTranslation.ingredients || null,
          preparation: Array.isArray(aiTranslation.preparation)
            ? aiTranslation.preparation
            : aiTranslation.preparation
              ? [aiTranslation.preparation]
              : null,
          chef_tip: aiTranslation.chef_tip || null,
          glycemic_notes: aiTranslation.glycemic_notes || null,
          peak_time_text: aiTranslation.peak_time_text || null,
          glucose_rise_text: aiTranslation.glucose_rise_text || null,
          tags: aiTranslation.tags || null,
        }, { onConflict: "recipe_id,language" })
        .then(({ error }) => {
          if (error) console.warn("[localizedRecipes] Failed to persist AI translation:", error.message);
        });
    }
    return sanitizeRecipeOutput(aiTranslation ?? baseRecipe, targetLanguage);
  } catch (err: any) {
    // Last resort: try to fetch the raw recipe without any translation
    try {
      const routeRecipeId = extractRouteRecipeId(recipeId);
      const lookupId = routeRecipeId || recipeId;
      if (isUUID(lookupId)) {
        const { data } = await supabase.from("recipes").select("*").eq("id", lookupId).maybeSingle();
        if (data) return sanitizeRecipeOutput(data);
      }
    } catch { /* ignore */ }
    return null;
  }
};

// Static ES→EN mapping for short recipe fields not stored in recipe_translations
const ES_EN_DIFFICULTY: Record<string, string> = {
  "muy fácil": "Very easy", "muy facil": "Very easy",
  "fácil": "Easy", "facil": "Easy",
  "medio": "Medium", "media": "Medium", "intermedio": "Medium", "intermedia": "Medium",
  "difícil": "Hard", "dificil": "Hard",
  "avanzado": "Advanced", "avanzada": "Advanced", "muy difícil": "Very hard", "muy dificil": "Very hard",
};

const ES_DE_DIFFICULTY: Record<string, string> = {
  "muy fácil": "Sehr leicht", "muy facil": "Sehr leicht",
  "fácil": "Leicht", "facil": "Leicht",
  "medio": "Mittel", "media": "Mittel", "intermedio": "Mittel", "intermedia": "Mittel",
  "difícil": "Schwer", "dificil": "Schwer",
  "avanzado": "Fortgeschritten", "avanzada": "Fortgeschritten", "muy difícil": "Sehr schwer", "muy dificil": "Sehr schwer",
};

const ES_EN_MEAL_MOMENT: Record<string, string> = {
  "desayuno": "Breakfast",
  "almuerzo": "Lunch", "comida": "Lunch",
  "cena": "Dinner",
  "merienda": "Snack", "snack": "Snack",
  "postre": "Dessert",
  "bebida": "Drink", "bebidas": "Drinks",
  "aperitivo": "Appetizer", "entrante": "Starter",
  "cualquier momento": "Any time", "todo el día": "All day",
};

const ES_DE_MEAL_MOMENT: Record<string, string> = {
  "desayuno": "Frühstück",
  "almuerzo": "Mittagessen", "comida": "Mittagessen",
  "cena": "Abendessen",
  "merienda": "Snack", "snack": "Snack",
  "postre": "Dessert",
  "bebida": "Getränk", "bebidas": "Getränke",
  "aperitivo": "Vorspeise", "entrante": "Vorspeise",
  "cualquier momento": "Jederzeit", "todo el día": "Den ganzen Tag",
};

function translateShortField(value: string, map: Record<string, string>): string {
  if (!value) return value;
  const key = value.toLowerCase().trim();
  return map[key] ?? value;
}

function translateServings(value: string): string {
  if (!value) return value;
  return value
    .replace(/\bporciones?\b/gi, (m) => (m.endsWith("s") || m.toLowerCase() === "porciones" ? "servings" : "serving"))
    .replace(/\bración\b/gi, "serving")
    .replace(/\braciones\b/gi, "servings")
    .replace(/\bpersonas?\b/gi, (m) => (m.toLowerCase() === "personas" ? "people" : "person"));
}

function translateServingsDe(value: string): string {
  if (!value) return value;
  return value
    .replace(/\bporciones?\b/gi, (m) => (m.endsWith("s") || m.toLowerCase() === "porciones" ? "Portionen" : "Portion"))
    .replace(/\bración\b/gi, "Portion")
    .replace(/\braciones\b/gi, "Portionen")
    .replace(/\bpersonas?\b/gi, (m) => (m.toLowerCase() === "personas" ? "Personen" : "Person"))
    .replace(/\bservings?\b/gi, (m) => (m.toLowerCase() === "servings" ? "Portionen" : "Portion"));
}

/**
 * Final sanitization pass before returning a recipe to the UI.
 * Ensures all fields that the render layer accesses are in expected types.
 */
function sanitizeRecipeOutput(recipe: any, language?: string): any {
  if (!recipe) return recipe;
  const normalizedPreparation = normalizeRecipeSteps(recipe.preparation);
  let ingredients = normalizeRecipeIngredients(recipe.ingredients);

  const lang = language || recipe.language;
  const isEn = lang === "en";
  const isDe = lang === "de";

  // Translate leftover Spanish unit names for EN users
  if (isEn) {
    ingredients = ingredients.map(translateSpanishUnits);
  }

  // Translate short fields that are not stored in recipe_translations
  const difficulty = isEn
    ? translateShortField(normalizeTextValue(recipe.difficulty), ES_EN_DIFFICULTY)
    : isDe
    ? translateShortField(normalizeTextValue(recipe.difficulty), ES_DE_DIFFICULTY)
    : normalizeTextValue(recipe.difficulty);
  const mealMoment = isEn
    ? translateShortField(normalizeTextValue(recipe.meal_moment), ES_EN_MEAL_MOMENT)
    : isDe
    ? translateShortField(normalizeTextValue(recipe.meal_moment), ES_DE_MEAL_MOMENT)
    : normalizeTextValue(recipe.meal_moment);
  const servings = isEn
    ? translateServings(normalizeTextValue(recipe.servings))
    : isDe
    ? translateServingsDe(normalizeTextValue(recipe.servings))
    : normalizeTextValue(recipe.servings);

  return {
    ...recipe,
    title: normalizeTextValue(recipe.title),
    description: normalizeTextValue(recipe.description),
    category: normalizeTextValue(recipe.category),
    chef_tip: normalizeTextValue(recipe.chef_tip),
    difficulty,
    meal_moment: mealMoment,
    servings,
    image: typeof recipe.image === "string" && recipe.image.trim() ? recipe.image.trim() : null,
    image_group: typeof recipe.image_group === "string" && recipe.image_group.trim() ? recipe.image_group.trim() : null,
    tags: ensureSafeArray(recipe.tags),
    ingredients,
    preparation: normalizedPreparation.join("\n"),
  };
}

/**
 * Batch-fetch N recipes by UUID + their translations in 2 queries instead of 2N.
 * Returns results in the same order as `ids`. Skips AI-translation fallback — suitable
 * for card lists (recently viewed, home feed) where best-effort translation is acceptable.
 */
export const getLocalizedRecipesByIds = async (ids: string[], targetLanguage: string): Promise<any[]> => {
  if (!ids.length) return [];

  const [recipesResult, translationsResult] = await Promise.all([
    supabase.from("recipes").select("*").in("id", ids),
    supabase.from("recipe_translations").select("*").in("recipe_id", ids).eq("language", targetLanguage),
  ]);

  if (recipesResult.error || !recipesResult.data) return [];

  const translationMap = new Map<string, any>();
  (translationsResult.data || []).forEach((t) => translationMap.set(t.recipe_id, t));

  const recipeMap = new Map<string, any>();
  recipesResult.data.forEach((r) => recipeMap.set(r.id, r));

  return ids
    .map((id) => {
      const recipe = recipeMap.get(id);
      if (!recipe) return null;
      const translation = translationMap.get(id);
      const merged = translation ? { ...recipe, ...translation, id: recipe.id } : recipe;
      return sanitizeRecipeOutput(merged, targetLanguage);
    })
    .filter(Boolean);
};
